import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../../store';
import { PermissionsUtil } from '../../../../utils/permissions/permissionsUtil';
import { PERMISSIONS } from '../../../../constants/permissions/Permissions.constants';
import React, { useEffect, useState } from 'react';
import { ACCOUNTING_DEFS } from '../../../../constants/i18n/translations/termSetDefinitions/accounting';
import _ from 'lodash';
import { getMaxSortOrderValue } from '../../../../store/accounting';
import { ColDef } from 'ag-grid-community';
import DefaultColumnTypes from '../../../../components/grids/columns/Column.constants';
import CustomHeader from '../../../../components/grids/CustomHeader';
import { FiscalYearStatusGridDefs } from '../../../../constants/i18n/translations/termDefinitions/accounting';
import { isNilOrEmpty } from '../../../../utils/objectUtils';
import { FiscalYearStatus } from '../../../../types/accounting/common/Periodicity';
import { auditableGridColumns } from '../../../grid/utils/auditableGrid/auditableGridColumns';
import {
    useListFiscalYearStatusQuery,
    useUpdateListFiscalYearStatusMutation,
} from '../../../../services/accounting/accounting.service';
import useTranslation from '../../../../components/i18n/hooks/useTranslation';
import { useNavigate } from 'react-router-dom';
import { RoutingValues } from '../../../../types/Shared.types';

export const useFiscalYearStatusGrid = () => {
    //translation
    const { termSet, isLoadingTermSet } = useTranslation(
        ACCOUNTING_DEFS.FISCAL_YEAR_STATUS_GRID
    );

    //permissions
    const user = useSelector((state: RootState) => state.user);
    const canView = PermissionsUtil.isPermissionEnabled(
        user.permissions,
        PERMISSIONS.ACCOUNTING.FISCAL_YEAR_STATUSES.VIEW
    );
    const canCreate = PermissionsUtil.isPermissionEnabled(
        user.permissions,
        PERMISSIONS.ACCOUNTING.FISCAL_YEAR_STATUSES.CREATE
    );
    const canUpdate = PermissionsUtil.isPermissionEnabled(
        user.permissions,
        PERMISSIONS.ACCOUNTING.FISCAL_YEAR_STATUSES.EDIT
    );

    //column and data definitions
    const [colDefs, setColDefs] = useState([]);
    const [rowData, setRowData] = useState([]);
    const [hiddenIdColumns, auditableColumns] = auditableGridColumns();

    const { data: entityList, isLoading: isLoading } =
        useListFiscalYearStatusQuery();
    const [updateList] = useUpdateListFiscalYearStatusMutation();

    useEffect(() => {
        if (!isNilOrEmpty(termSet)) {
            const columnDefs: ColDef[] = [
                ...hiddenIdColumns,
                {
                    field: 'sortOrder',
                    sort: 'asc',
                    ...DefaultColumnTypes.NumberOnly,
                    headerComponentFramework: (props: any) => {
                        return (
                            <CustomHeader
                                {...props}
                                termSet={termSet}
                                termKey={FiscalYearStatusGridDefs.SortOrder}
                            />
                        );
                    },
                },
                {
                    field: 'code',
                    ...DefaultColumnTypes.ShortText,
                    headerComponentFramework: (props: any) => {
                        return (
                            <CustomHeader
                                {...props}
                                termSet={termSet}
                                termKey={FiscalYearStatusGridDefs.Code}
                            />
                        );
                    },
                },
                {
                    field: 'description',
                    ...DefaultColumnTypes.LongText,
                    headerComponentFramework: (props: any) => {
                        return (
                            <CustomHeader
                                {...props}
                                termSet={termSet}
                                termKey={FiscalYearStatusGridDefs.Description}
                            />
                        );
                    },
                },
                {
                    field: 'closed',
                    ...DefaultColumnTypes.Boolean,
                    headerComponentFramework: (props: any) => {
                        return (
                            <CustomHeader
                                {...props}
                                termSet={termSet}
                                termKey={FiscalYearStatusGridDefs.Closed}
                            />
                        );
                    },
                },
                ...auditableColumns,
            ];
            setColDefs(columnDefs);
        }
    }, [termSet]);

    useEffect(() => {
        if (!isNilOrEmpty(entityList)) {
            setRowData(entityList);
        }
    }, [entityList]);

    const isLoadingData = () => isLoading && isLoadingTermSet;

    //sortOrder
    const dispatch = useDispatch();
    const handleGridEdits = (editedRows: FiscalYearStatus[]) => {
        updateList(editedRows);
    };
    useEffect(() => {
        let maxValue = 1;
        if (entityList && entityList.length > 0) {
            const data = _.maxBy(
                entityList,
                (yearStatus: FiscalYearStatus) => yearStatus.sortOrder
            );
            maxValue = data.sortOrder + 1;
        }
        dispatch(
            getMaxSortOrderValue({ isLoading: false, maxValue: maxValue })
        );
    }, entityList);

    //redirect
    const navigate = useNavigate();
    const redirectToForm = (row: any) => {
        if (row.id) {
            navigate(`/accounting/setup/fiscalYearStatus/${row.id}`);
        } else {
            navigate(
                `/accounting/setup/fiscalYearStatus/${RoutingValues.newId}`
            );
        }
    };

    return {
        canView,
        canUpdate,
        canCreate,
        termSet,
        rowData,
        colDefs,
        handleGridEdits,
        isLoadingData,
        redirectToForm,
    };
};

export default useFiscalYearStatusGrid;
